// Crawl state for the app: the form, planning, shared links, and keeping the
// current crawl (in this browser) until it's ended. The planner
// itself (scoring, the worker) is loaded on first use from ./engine.

import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_DWELL, DEFAULT_MAX_LEG, DEFAULT_STOPS } from './config';
import type { AppData } from './engine';
import type { CrawlLink } from './link';
import type { BetterStart, Crawl, CrawlOptions, CrawlStart, PlanResult } from './plan';
import { clearSavedCrawl, loadSavedCrawl, saveCrawl } from './saved';
import { nextQuarter } from './times';
import type { Filters } from '../lib/filters';

export interface CrawlForm extends Omit<CrawlOptions, 'start'> {
  start: CrawlStart | null;
}

export type ShortResult = Extract<PlanResult, { status: 'short' }>;

/** A crawl on the map: planned here, opened from a link, or brought back from storage. */
export interface CurrentCrawl {
  crawl: Crawl;
  shared: boolean;
  missing: number;
  better: BetterStart | null;
}

/** What the planner is doing (the current crawl, if any, stays put meanwhile). */
export type CrawlState =
  | { status: 'idle' }
  | { status: 'working'; what: 'plan' | 'shared' | 'saved' }
  | { status: 'short'; result: ShortResult; form: CrawlForm }
  /** retry: what to load again (a shared link, or the crawl saved here). */
  | { status: 'error'; message: string; retry?: CrawlLink | 'saved' };

export function defaultForm(filters: Filters, now = Date.now()): CrawlForm {
  return {
    start: null,
    stops: DEFAULT_STOPS,
    maxLeg: DEFAULT_MAX_LEG,
    maxTotal: null,
    startTime: nextQuarter(now),
    dwell: DEFAULT_DWELL,
    mode: 'best',
    filters: {
      confirmed: filters.confirmed,
      dog: filters.dog,
      ale: filters.ale,
      food: filters.food,
    },
  };
}

export function useCrawl(app: AppData | null, mapFilters: Filters) {
  // Read once: the saved crawl (if any) and its settings.
  const [saved] = useState(() => loadSavedCrawl(defaultForm(mapFilters)));
  const [form, setForm] = useState<CrawlForm>(() => saved?.form ?? defaultForm(mapFilters));
  const [state, setState] = useState<CrawlState>({ status: 'idle' });
  const [current, setCurrent] = useState<CurrentCrawl | null>(null);
  /** Showing the form while there's a crawl to go back to. */
  const [editing, setEditing] = useState(false);
  const generation = useRef(0);
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  /** Put a crawl on the map and keep it in this browser until it's ended. */
  const keep = useCallback((next: CurrentCrawl, settings: CrawlForm) => {
    setCurrent(next);
    setEditing(false);
    setState({ status: 'idle' });
    const { crawl } = next;
    saveCrawl({
      ids: crawl.stops.map((s) => s.pub.id),
      startTime: crawl.startTime,
      dwell: crawl.dwell,
      start: crawl.start,
      mode: crawl.mode,
      shared: next.shared,
      form: settings,
    });
  }, []);

  // Settings changed while a crawl is kept: remember them too.
  useEffect(() => {
    const kept = loadSavedCrawl(form);
    if (kept) saveCrawl({ ...kept, form });
  }, [form]);

  const plan = useCallback(
    (next: CrawlForm) => {
      const run = ++generation.current;
      if (!app) return;
      const start = next.start;
      if (!start) {
        setState({ status: 'error', message: 'Choose where you’re starting first.' });
        return;
      }
      // A start time left behind while the panel was open moves up to now.
      const startTime = Math.max(next.startTime, nextQuarter(Date.now() - 14 * 60_000));
      const form = { ...next, startTime };
      setForm(form);
      setState({ status: 'working', what: 'plan' });
      import('./engine')
        .then((engine) => engine.plan({ ...form, start }, app))
        .then((result) => {
          if (run !== generation.current) return;
          if (result.status === 'ok')
            keep({ crawl: result.crawl, shared: false, missing: 0, better: result.better }, form);
          else setState({ status: 'short', result, form });
        })
        .catch(() => {
          if (run === generation.current)
            setState({
              status: 'error',
              message: 'Couldn’t plan a crawl just now. Check your connection and try again.',
            });
        });
    },
    [app, keep],
  );

  const openShared = useCallback(
    (link: CrawlLink) => {
      const run = ++generation.current;
      if (!app) return;
      setState({ status: 'working', what: 'shared' });
      import('./engine')
        .then((engine) => engine.loadShared(link, app))
        .then(({ crawl, missing }) => {
          if (run !== generation.current) return;
          if (crawl.stops.length === 0) {
            setState({
              status: 'error',
              message:
                'None of the pubs on this shared crawl are in our data any more. Plan a new one below.',
            });
            return;
          }
          keep({ crawl, shared: true, missing, better: null }, formRef.current);
        })
        .catch(() => {
          if (run === generation.current)
            setState({
              status: 'error',
              message: 'Couldn’t load that shared crawl. Check your connection and try again.',
              retry: link,
            });
        });
    },
    [app, keep],
  );

  /** Bring back the crawl saved in this browser (after a reload). */
  const restore = useCallback(() => {
    const kept = loadSavedCrawl(formRef.current);
    if (!kept || !app) return;
    const run = ++generation.current;
    setState({ status: 'working', what: 'saved' });
    import('./engine')
      .then((engine) =>
        engine.loadShared({ ids: kept.ids, start: kept.startTime, dwell: kept.dwell }, app, {
          start: kept.start,
          mode: kept.mode,
        }),
      )
      .then(({ crawl, missing }) => {
        if (run !== generation.current) return;
        if (crawl.stops.length === 0) {
          clearSavedCrawl();
          setState({
            status: 'error',
            message:
              'None of the pubs on your crawl are in our data any more. Plan a new one below.',
          });
          return;
        }
        keep({ crawl, shared: kept.shared, missing, better: null }, kept.form);
      })
      .catch(() => {
        // Keep it saved: it'll load once the connection's back.
        if (run === generation.current)
          setState({
            status: 'error',
            message: 'Couldn’t load your crawl just now. Check your connection and try again.',
            retry: 'saved',
          });
      });
  }, [app, keep]);

  const retry = useCallback(() => {
    if (state.status !== 'error' || !state.retry) return;
    if (state.retry === 'saved') restore();
    else openShared(state.retry);
  }, [state, restore, openShared]);

  /** Back to the form; the crawl stays on the map until a new one replaces it. */
  const edit = useCallback(() => {
    generation.current++;
    setState({ status: 'idle' });
    setEditing(true);
  }, []);

  /** Back from the form to the crawl you already have. */
  const resume = useCallback(() => {
    generation.current++;
    setState({ status: 'idle' });
    setEditing(false);
  }, []);

  /** Done with it: off the map and out of storage. */
  const end = useCallback(() => {
    generation.current++;
    clearSavedCrawl();
    setCurrent(null);
    setEditing(false);
    setState({ status: 'idle' });
  }, []);

  return {
    form,
    setForm,
    state,
    current,
    editing,
    /** A crawl was saved here before this page loaded. */
    hadSaved: saved !== null,
    plan,
    openShared,
    restore,
    retry,
    edit,
    resume,
    end,
  };
}
